/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import { Box, TextField, Grid, Card, CardContent, Typography, Divider, Link } from "@mui/material";

import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import Autocomplete from "@mui/material/Autocomplete";
import { TransferMode, TransferModeMap, transferModeMappings } from "../../utils/Constants";
import { useWindowDimensions } from "../../utils/Hooks";
import { EnumDictionary, Setter } from "../../utils/Interfaces";
import InventoryIngress from "./InventoryIngress";
import InventoryTransfers from "./InventoryTransfers";
import InventoryTransfersDefective from "./InventoryTransfersDefective";
import { NavigateFunction, useNavigate } from "react-router-dom";
import InventoryAdmin from "./InventoryAdmin";

export interface TransferModeFormProps {
    onTransferred: Setter<string>;
}

const MaterialTransfers = () => {
    const navigate: NavigateFunction = useNavigate();

    const { width } = useWindowDimensions();
    const mobile: boolean = width <= 600;

    const [mode, setMode] = useState<TransferModeMap>(transferModeMappings[0]);

    const transferComponentMap: EnumDictionary<TransferMode, JSX.Element> = {
        [TransferMode.INGRESS]: <InventoryIngress />,
        [TransferMode.TRANSFER]: <InventoryTransfers />,
        [TransferMode.EGRESS]: (
            <Box sx={{ mt: 2 }}>
                <Typography>
                    Inventory Egress is handled by technicians through the&nbsp;
                    <Link onClick={() => navigate("/tech-dash")} sx={{ cursor: "pointer" }}>
                        Service Dashboard
                    </Link>
                    &nbsp;or&nbsp;
                    <Link onClick={() => navigate("/projects")} sx={{ cursor: "pointer" }}>
                        Projects
                    </Link>
                    .
                </Typography>
            </Box>
        ),
        [TransferMode.DEFECTIVE]: <InventoryTransfersDefective />,
        [TransferMode.ADMIN]: <InventoryAdmin />,
    };

    return (
        <Box>
            <StyledBreadcrumbs
                paths={[
                    { text: "Techwise Portal", route: "/" },
                    { text: "Materials", route: "/?section=materials" },
                    { text: "Transfers", route: "/materials/transfers" },
                ]}
            />

            <Card sx={{ mt: 1 }}>
                <CardContent>
                    <Grid spacing={3} container>
                        <Grid item xs={mobile ? 7 : 9}>
                            <Typography variant={mobile ? "h6" : "h5"}>{mode.name}</Typography>
                        </Grid>
                        <Grid item xs={mobile ? 5 : 3}>
                            <Autocomplete
                                disablePortal
                                onChange={(_event, value) => {
                                    setMode(value ? value : transferModeMappings[0]);
                                }}
                                size="small"
                                id="transferMode"
                                getOptionKey={(option: TransferModeMap) => option.abbrev}
                                options={transferModeMappings}
                                value={mode}
                                getOptionLabel={(option: TransferModeMap) => option.abbrev}
                                renderInput={(params) => <TextField required {...params} label="Transfer Mode" />}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ mt: 3 }} />

                    {transferComponentMap[mode.value]}
                </CardContent>
            </Card>
        </Box>
    );
};

export default MaterialTransfers;
